import { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import { useHistory } from 'react-router-dom';
import styled from "styled-components";
import lodash from 'lodash';

import TotalModalWrapper from '../../../../Share/Normal/TotalModalWrapper';
import FetchModule from "../../../../Share/Network/FetchModule";

import WhiteTrashIcon from '../../../../../image/WhiteTrashIcon.png';
import PaymentOptionSettingSelectBox from "../../../../../MemberRegistration/Model/PaymentOptionSettingSelectBox";
import PaymentOptionCustomInput from "../../../../../MemberRegistration/Component/PaymentOptionCustomInput";
import DiscountAmountInput from "../../../../../Component/MemberComponent/DiscountAmountInput";
import ProductCountInput from "../../../../../Component/MemberComponent/ProductCountInput";
import AddPaymentOptionModal from "../../../../../MemberRegistration/Model/AddPaymentOptionModal";
import { useLanguage } from '../../../../../Context/LanguageContext';
import { useTheme } from '../../../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../../../Styles/theme';

const ProductPaymentModal = forwardRef(({ clickData: clickDataProp, clickUsageData, memberName, totalPoint, onRegist, onClickExit }, ref) => {
  const history = useHistory();
  const { t, isEng } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const DefaultPaymentOption = [{ id: 0, name: '현금' }, { id: 0, name: '카드' }, { id: 0, name: '포인트' }];

  const [title, setTitle] = useState(
    clickUsageData !== undefined
      ? `${memberName} - ${t('memberDetail.productSalesModal.productSalesEditHistory')}`
      : `${memberName} - ${t('memberDetail.productSalesModal.productSalesAddProduct')}`
  );
  const [clickData, setClickData] = useState(clickDataProp);
  const [count, setCount] = useState(1);
  const [discount, setDiscount] = useState(0);
  const [notReceived, setNotReceived] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLinkPay, setIsLinkPay] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [linkoPayIndicator, setLinkoPayIndicator] = useState(null);
  const [isClickedAddPaymentOption, setIsClickedAddPaymentOption] = useState(false);
  const [paymentOptions, setPaymentOptions] = useState([...DefaultPaymentOption]);
  const [selectedPaymentOptions, setSelectedPaymentOptions] = useState([{ id: 0, name: '현금', price: 0 }]);
  const [modalHeight, setModalHeight] = useState(321);

  const ModalRef = useRef();

  useImperativeHandle(ref, () => ({
    getProductData: () => clickData,
    getProductId: () => clickData.id,
    getProductName: () => clickData.name,
    getCount: () => count,
    getCardPrice: () => 0,
    getCashPrice: () => 0,
    getDiscountPrice: () => discount,
    getNotReceivedPrice: () => notReceived,
    getSelectedPaymentOptions: () => selectedPaymentOptions,
    getPoint: () => 0,
  }));

  // 수정 모드: 상품 조회 및 기존 데이터 초기화 //
  useEffect(() => {
    if (clickUsageData !== undefined) {
      const fetchModule = new FetchModule();
      (async () => {
        const responseData = await fetchModule.getIndividualData('product/', 'GET');
        if (responseData.status === 200) {
          const list = responseData.data.slice();
          const saledProduct = list.find(p => p.id === clickUsageData.productId);
          setClickData(saledProduct);

          let selectedPaymentOptionLength = 0;
          if (clickUsageData.selectedPaymentOptions?.length > 0) {
            // console.log('[ProductPaymentModal] selectedPaymentOptions from API:', JSON.stringify(clickUsageData.selectedPaymentOptions));
            selectedPaymentOptionLength = clickUsageData.selectedPaymentOptions.length - 1;
            setSelectedPaymentOptions(lodash.cloneDeep(clickUsageData.selectedPaymentOptions));
          } else {
            // console.log('[ProductPaymentModal] selectedPaymentOptions empty, falling back to cardPrice/cashPrice');
            const newOpts = [];
            if (clickUsageData.cashPrice !== 0) newOpts.push({ id: 0, name: '현금', price: clickUsageData.cashPrice });
            if (clickUsageData.cardPrice !== 0) newOpts.push({ id: 0, name: '카드', price: clickUsageData.cardPrice });
            if (clickUsageData.point !== 0) newOpts.push({ id: 0, name: '포인트', price: clickUsageData.point });
            if (newOpts.length > 0) {
              selectedPaymentOptionLength = newOpts.length - 1;
              setSelectedPaymentOptions(newOpts);
            }
          }

          setNotReceived(clickUsageData.notReceivedPrice);
          setDiscount(clickUsageData.discountPrice);
          setCount(clickUsageData.count);

          if (clickUsageData.merchantUid) {
            const indicator = clickUsageData.merchantUid.split('-')[2];
            if (indicator === '1' || indicator === '2') {
              setIsLinkPay(true);
              setLinkoPayIndicator(indicator);
              if (clickUsageData.isPending) {
                setIsPending(true);
                setTitle(`${memberName} - ${t('memberDetail.productSalesModal.productSalesEditHistory')}`);
              } else {
                setTitle(`${memberName} - ${t('memberDetail.productSalesModal.productSalesSelectProduct')}`);
              }
            }
          }

          setModalHeight(321 + (63 * selectedPaymentOptionLength));

          setTimeout(() => {
            ModalRef.current.style.opacity = 1;
            ModalRef.current.style.transition = 'all 0.3s ease-in-out';
          }, 50);
        }
      })();
    } else {
      setTimeout(() => {
        ModalRef.current.style.opacity = 1;
        ModalRef.current.style.transition = 'all 0.3s ease-in-out';
      }, 100);
    }
  }, []);

  // 결제수단 데이터 불러오기 //
  useEffect(() => {
    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.getDataVersion2('payment_option/', 'GET');
      if (responseData.status === 200) {
        setPaymentOptions(DefaultPaymentOption.concat(responseData.data.paymentOptions));
      } else if (responseData.status === 401) history.push('/');
    })();
  }, []);

  // 미수금 산정하기 //
  useEffect(() => {
    if (clickData === undefined) { setNotReceived(0); return; }

    let totalPrice = 0;
    for (let i = 0; i < selectedPaymentOptions.length; i++) {
      if (selectedPaymentOptions[i].price !== '') totalPrice += parseInt(selectedPaymentOptions[i].price ?? 0);
    }
    if (discount !== '') totalPrice += parseInt(discount.toString().replaceAll(',', ''));
    setNotReceived(Math.max((clickData.price * count) - totalPrice, 0));
  }, [clickData, count, selectedPaymentOptions, discount]);

  // 추가 모드에서 물품 선택 시 총금액 자동 입력 //
  useEffect(() => {
    if (clickData === undefined || clickUsageData !== undefined) return;
    setSelectedPaymentOptions([{ id: 0, name: '현금', price: clickData.price * count }]);
    setDiscount(0);
    setModalHeight(321);
  }, [clickData]);

  function onChangeCount(newCount) {
    setCount(newCount);
    if (clickData === undefined) return;
    setSelectedPaymentOptions([{ id: 0, name: '현금', price: clickData.price * newCount }]);
    setDiscount(0);
    setModalHeight(321);
  }

  function onCalcIsExistProductView() {
    if (clickData === undefined) return true;
    return clickData.picture === '';
  }

  function onCalcProductView() {
    if (clickData === undefined) return '';
    if (clickData.picture === '') return 'https://picture.link-zone.org/DefaultLogo.png';
    return clickData.picture;
  }

  function onCalcProductName() {
    return clickData ? clickData.name : '';
  }

  function onCalcProductInventory() {
    return clickData ? clickData.inventory.toLocaleString('ko-KR') : '0';
  }

  function onCalcProductPrice() {
    return clickData ? clickData.price.toLocaleString('ko-KR') : '0';
  }

  function onCalcProductSalePrice() {
    return clickData ? (clickData.price * count).toLocaleString('ko-KR') : '0';
  }

  function onClickSave() {
    if (notReceived > 0) {
      alert(t('memberDetail.productSalesModal.alertZeroPaymentAmount'));
      return;
    }

    if (clickData) {
      let totalPayment = 0;
      for (let i = 0; i < selectedPaymentOptions.length; i++) {
        if (selectedPaymentOptions[i].price !== '') {
          totalPayment += parseInt(selectedPaymentOptions[i].price ?? 0);
        }
      }
      const discountAmount = discount !== '' ? parseInt(discount.toString().replaceAll(',', '')) : 0;
      totalPayment += discountAmount;

      if (totalPayment > clickData.price * count) {
        alert(t('memberDetail.productSalesModal.alertOverPaymentAmount'));
        return;
      }
    }

    onRegist();
  }

  function onClickAdditionOption() {
    setIsClickedAddPaymentOption(true);
  }

  // 결제수단 모달 내에서 저장하기 클릭시 //
  function onClickSavePaymentOption(optionName) {
    if (optionName === '') {
      alert(t('memberDetail.productSalesModal.alertEnterPaymentName'));
      return;
    }
    if (isLoading) {
      alert(t('memberDetail.productSalesModal.alertSavingInProgress'));
      return;
    }
    setIsLoading(true);

    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.postDataVersion2('payment_option/save', 'POST', { optionName });
      if (responseData.status === 200) {
        setPaymentOptions(DefaultPaymentOption.concat(responseData.data.paymentOptions));
        setIsLoading(false);
        setIsClickedAddPaymentOption(false);
      } else if (responseData.status === 401) history.push('/');
      else {
        alert(responseData.error);
        setIsLoading(false);
      }
    })();
  }

  // 결제 수단 삭제버튼 클릭시 //
  function onClickDeleteOption(paymentOptionId) {
    if (isLoading) {
      alert(t('memberDetail.alertDeletingInProgress'));
      return;
    }
    setIsLoading(true);

    const fetchModule = new FetchModule();
    (async () => {
      const responseData = await fetchModule.postDataVersion2('payment_option/delete', 'POST', { paymentOptionId });
      if (responseData.status === 200) {
        setPaymentOptions(DefaultPaymentOption.concat(responseData.data.paymentOptions));
        setIsLoading(false);
        setIsClickedAddPaymentOption(false);
      } else if (responseData.status === 401) history.push('/');
      else {
        alert(responseData.error);
        setIsLoading(false);
      }
    })();
  }

  // 결제 수단 변경시 //
  function onChangeSelectedPaymentOptions(data, index) {
    const newOpts = selectedPaymentOptions.slice();
    newOpts[index].id = data.id;
    newOpts[index].name = data.name;
    setSelectedPaymentOptions(newOpts);
  }

  // 결제 금액 변경시 //
  function onChangePaymentOptionPrice(value, index) {
    const newOpts = selectedPaymentOptions.slice();
    newOpts[index].price = value.replaceAll(',', '');
    setSelectedPaymentOptions(newOpts);
  }

  // 결제 수단 추가 클릭시 //
  function onClickAddPaymentOptions() {
    const newOpts = [...selectedPaymentOptions, { id: 0, name: '현금', price: notReceived }];
    setSelectedPaymentOptions(newOpts);
    setModalHeight(321 + (63 * (newOpts.length - 1)));
  }

  // 결제 수단 제거 클릭시 //
  function onClickDeletePaymentOptions(index) {
    const newOpts = selectedPaymentOptions.slice();
    newOpts.splice(index, 1);
    setSelectedPaymentOptions(newOpts);
    setModalHeight(321 + (63 * (newOpts.length - 1)));
  }

  return (
    <TotalModalWrapper>
      {isClickedAddPaymentOption && <AddPaymentOptionModal onClickSave={onClickSavePaymentOption} onClickExit={() => setIsClickedAddPaymentOption(false)} />}
      <Modal ref={ModalRef} $isDark={isDark}>
        <InnerWrapper>
          <TitleWrapper>
            <TitleTextWrapper>
              <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text} hover='default'>{title}</Text>
            </TitleTextWrapper>
          </TitleWrapper>
          <ProductSalesView>
            <ProductBoxView>
              <ProductImageWrapper>
                <ProductImage isEmpty={onCalcIsExistProductView()} src={onCalcProductView()} />
              </ProductImageWrapper>
              <ProductInfoBox>
                <ProductInfoWrapper>
                  <ProductTextWrapper alignItems='flex-start'>
                    <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text} $ellipsis>{onCalcProductName()}</Text>
                  </ProductTextWrapper>
                </ProductInfoWrapper>
                <ProductInfoWrapper>
                  <ProductTextWrapper alignItems='flex-end'>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text}>{t('memberDetail.productSalesModal.productSalesQuantity')}</Text>
                  </ProductTextWrapper>
                  <TextRowWrapper>
                    <Text fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.text}>{onCalcProductInventory()}</Text>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text}>{t('memberDetail.productSalesModal.productSalesPieceUnit')}</Text>
                  </TextRowWrapper>
                </ProductInfoWrapper>
                <ProductInfoWrapper>
                  <ProductTextWrapper alignItems='flex-start'>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={16} color={theme.text}>{t('memberDetail.productSalesModal.productSalesPrice')}</Text>
                  </ProductTextWrapper>
                  <TextRowWrapper>
                    {isEng && <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('memberDetail.productSalesModal.productSalesPriceUnit')}</Text>}
                    <Text fontFamily='NotoSansKR-Medium' fontSize={16} color={theme.text}>{onCalcProductPrice()}</Text>
                    {!isEng && <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text}>{t('memberDetail.productSalesModal.productSalesPriceUnit')}</Text>}
                  </TextRowWrapper>
                </ProductInfoWrapper>
              </ProductInfoBox>
            </ProductBoxView>
            <ProductSalesInfoBox>
              <LayerWrapper>
                <ComponentWrapper>
                  <ComponentTitle>{t('memberDetail.productSalesModal.productSalesItemPrice')}</ComponentTitle>
                  <CalcPaymentInputWrapper>
                    {isEng && <CalcPaymentUnit>{t('memberDetail.refundModal.won')}</CalcPaymentUnit>}
                    <CalcPaymentInput style={{ justifyContent: isEng ? 'flex-start' : 'flex-end' }}>{onCalcProductSalePrice()}</CalcPaymentInput>
                    {!isEng && <CalcPaymentUnit>{t('memberDetail.refundModal.won')}</CalcPaymentUnit>}
                  </CalcPaymentInputWrapper>
                </ComponentWrapper>
                <ComponentWrapper>
                  <ComponentTitle>{t('memberDetail.productSalesModal.productSalesPurchaseCount')}</ComponentTitle>
                  <ProductCountInput value={count} setValue={onChangeCount} disabled={isLinkPay && !isPending} isBgColor={true}/>
                </ComponentWrapper>
              </LayerWrapper>
              {selectedPaymentOptions.map((data, index) => (
                <InnerLayerWrapper
                  key={'SelectedPaymentOptions-' + index}
                  marginTop={10}
                  gap={20}
                  style={{ zIndex: selectedPaymentOptions.length - index }}>
                  <InputWrapper>
                    <PaymentOptionTitleWrapper>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text} cursor='default'>{t('memberDetail.productSalesModal.paymentMethod') + (index === 0 ? '' : ' ' + (index + 1))}</Text>
                      {(!isLinkPay || isPending || data.name === '포인트') && index !== 0 &&
                        <DeleteButton onClick={() => onClickDeletePaymentOptions(index)}>
                          <DeleteIcon src={WhiteTrashIcon} />
                        </DeleteButton>
                      }
                    </PaymentOptionTitleWrapper>
                    {isLinkPay && !isPending && data.name !== '포인트' ?
                      <LinkoPayTextView $isEdit={clickUsageData !== undefined}>
                        <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='default'>
                          {data.name === '링코페이' ? t('memberDetail.productSalesModal.linkoPay') : data.name}
                        </Text>
                      </LinkoPayTextView>
                      :
                      <PaymentOptionSettingSelectBox
                        index={index}
                        value={data}
                        setValue={onChangeSelectedPaymentOptions}
                        options={paymentOptions}
                        disabled={paymentOptions.length === 0}
                        height={35}
                        onClickAdditionOption={onClickAdditionOption}
                        onClickDeleteOption={onClickDeleteOption}
                        includeLinkoPayOption={true} />
                    }
                  </InputWrapper>
                  <InputWrapper>
                    <PaymentOptionTitleWrapper>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text} cursor='default'>{t('memberDetail.productSalesModal.receivedPrice')}</Text>
                      {(data.paymentOptionId === 0 || (data.paymentOptionId === undefined && data.id === 0)) && data.name === '포인트' &&
                        <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subKey} cursor='default'>{totalPoint.toLocaleString('ko-KR') + 'P'}</Text>
                      }
                    </PaymentOptionTitleWrapper>
                    <UnitInputWrapper>
                      {isEng && <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text} cursor='default'>{t('memberDetail.refundModal.won')}</Text>}
                      <PaymentOptionCustomInput
                        index={index}
                        value={(data.price ?? 0).toLocaleString('ko-KR')}
                        setValue={onChangePaymentOptionPrice}
                        placeholder='0'
                        disabled={isLinkPay && !isPending && data.name !== '포인트'}
                        height={35}
                        textAlign={isEng ? 'left' : 'right'} />
                      {!isEng && <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text} cursor='default'>{t('memberDetail.refundModal.won')}</Text>}
                    </UnitInputWrapper>
                  </InputWrapper>
                </InnerLayerWrapper>
              ))}
              {(!isLinkPay || isPending) &&
                <AddPaymentOptionButton onClick={onClickAddPaymentOptions}>{t('memberDetail.productSalesModal.addPaymentOption')}</AddPaymentOptionButton>
              }
              {!(isLinkPay && !isPending && clickUsageData !== undefined) &&
              <InnerLayerWrapper marginTop={20} gap={20}>
                <InputWrapper>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text} cursor='default'>{t('memberDetail.membershipAddModal.discountAmount')}</Text>
                  <UnitInputWrapper>
                    <DiscountAmountInput
                      value={discount.toLocaleString('ko-KR')}
                      setValue={setDiscount}
                      placeholder='0'
                      height={35}/>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.subKey} cursor='default'>{t('memberDetail.refundModal.won')}</Text>
                  </UnitInputWrapper>
                </InputWrapper>
                <RowDivisionLine />
                <InputWrapper>
                  <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.text} cursor='default'>{t('memberDetail.productSalesModal.productSalesUnpaid')}</Text>
                  <UnitInputWrapper>
                    <NotReceivedInputView>
                      <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.pointRed} cursor='default'>{notReceived.toLocaleString('ko-KR')}</Text>
                    </NotReceivedInputView>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={12} color={theme.pointRed} cursor='default'>{t('memberDetail.refundModal.won')}</Text>
                  </UnitInputWrapper>
                </InputWrapper>
              </InnerLayerWrapper>}
            </ProductSalesInfoBox>
          </ProductSalesView>
          <SaveButtonWrapper>
            <ExitButton onClick={onClickExit}>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} cursor='pointer'>{t('memberDetail.modalCancel')}</Text>
            </ExitButton>
            {!selectedPaymentOptions.some(opt => opt.name === '링코페이') && (linkoPayIndicator !== '1' || isPending) &&
              <SaveButton onClick={onClickSave}>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subBackground} cursor='pointer'>{t('memberDetail.productSalesModal.btnSave')}</Text>
              </SaveButton>
            }
            {(selectedPaymentOptions.some(opt => opt.name === '링코페이') || (linkoPayIndicator === '1' && !isPending)) &&
              <LinkoPayButton onClick={onClickSave}>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subBackground} cursor='pointer'>{t('memberDetail.productSalesModal.btnSendLinkoPay')}</Text>
              </LinkoPayButton>
            }
          </SaveButtonWrapper>
        </InnerWrapper>
      </Modal>
    </TotalModalWrapper>
  );
});

export default ProductPaymentModal;

const Modal = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 20px;
  opacity: 0;

  width: 556px;
  box-sizing: border-box;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const InnerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  margin-bottom: 30px;

  width: 100%;
`;

const TitleTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 4px;
`;

const SaveButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  width: 100%;

  gap: 10px;
`;

const ExitButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 0 27px;

  height: 40px;

  white-space: nowrap;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const SaveButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 40px;

  border-radius: 5px;
  background-color: ${props => props.theme.primary};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const LinkoPayButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 40px;

  border-radius: 5px;
  background-color: ${props => props.theme.subKey};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ProductSalesView = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  margin-bottom: 30px;

  width: 100%;

  overflow: hidden;

  transition: all 0.3s ease-in-out;
`;

const ProductBoxView = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 190px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const ProductImageWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 190px;
  height: 190px;

  overflow: hidden;

  border-radius: 10px 10px 0px 0px;
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;

  object-fit: cover;
  object-position: center;
`;

const ProductInfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  padding: 5px 10px;

  width: 100%;

  box-sizing: border-box;

  gap: 5px;

  border-radius: 0px 0px 10px 10px;
  background-color: ${props => props.theme.subBackground};
`;

const ProductInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 24px;
`;

const ProductTextWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: ${props => props.alignItems};

  width: ${props => props.width}%;
  height: 100%;

  overflow: hidden;
`;

const ProductSalesInfoBox = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 296px;
`;

const LayerWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;

  gap: 20px;
`;

const InnerLayerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-top: ${props => props.marginTop}px;

  width: 100%;

  gap: ${props => props.gap}px;
`;

const ComponentWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
`;

const ComponentTitle = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 12px;

  color: ${props => props.theme.text};

  margin: 0 0 0 0;

  width: 100%;
`;

const CalcPaymentInputWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-top: 8px;

  width: 100%;

  gap: 5px;
`;

const CalcPaymentInput = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  padding: 0 8px 0 8px;
  margin: 0 0 0 0;

  width: 100%;
  height: 35px;

  border: 1px solid ${props => props.theme.subBackground};
  border-radius: 5px;
  background-color: ${props => props.theme.subBackground};

  font-family: NotoSansKR-Regular;
  font-size: 14px;

  color: ${props => props.theme.text};
`;

const CalcPaymentUnit = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 12px;

  margin: 0 0 0 0;

  color: ${props => props.theme.text};
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;

  gap: 4px;
`;

const PaymentOptionTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const UnitInputWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;

  gap: 4px;
`;

const NotReceivedInputView = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: center;

  padding: 0 8px;

  width: 100%;
  height: 35px;

  border: 1px solid ${props => props.theme.subBackground};
  border-radius: 5px;
  background-color: ${props => props.theme.subBackground};
`;

const LinkoPayTextView = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  padding: ${props => props.$isEdit ? '0 8px' : '0'};
  box-sizing: border-box;
  width: 100%;
  height: 35px;

  border-radius: 5px;
  background-color: ${props => props.$isEdit ? props.theme.buttonFill : 'transparent'};
`;

const AddPaymentOptionButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  margin-top: 20px;

  width: 100%;
  height: 40px;

  border-radius: 20px;
  background-color: ${props => props.theme.buttonFill};

  font-family: NotoSansKR-Bold;
  font-size: 14px;

  color: ${props => props.theme.subText};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      color: ${props => props.theme.subBackground};
      background-color: ${props => props.theme.subKey};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const DeleteButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 24px;
  height: 18px;

  border-radius: 2px;
  background-color: ${props => props.theme.pointRedAlpha50};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.theme.pointRed};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const DeleteIcon = styled.img`
  width: 14px;
  height: 14px;
`;

const TextRowWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-end;
  align-items: baseline;

  gap: 2px;
`;

const RowDivisionLine = styled.div`
  flex-shrink: 0;
  width: 1px;
  height: 54px;

  background-color: ${props => props.theme.line};
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  padding: 0 0 0 0;

  ${props => props.$ellipsis && `
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `}

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: ${props => props.cursor || 'default'};
    }
  }
`;
